
import React, { createContext, useContext, useState, useEffect, ReactNode, useRef, useCallback, useMemo } from 'react';

import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useQueryClient } from '@tanstack/react-query';
import { AuthContext } from '@/contexts/AuthContext';
type Hotel = {
  id: string;
  name: string;
  slug: string;
  subdomain?: string;
  is_active: boolean;
};

type OptimizedHotelContextType = {
  currentHotel: Hotel | null;
  hotels: Hotel[];
  loading: boolean;
  hotelReady: boolean;
  isLoading: boolean;
  availableHotels: Hotel[];
  userProfile: any;
  switchHotel: (hotelId: string) => Promise<void>;
  refreshHotels: () => Promise<void>;
  refreshContext: () => Promise<void>;
  canSwitchHotels: boolean;
  isSuperAdmin: boolean;
  riskScore: number;
  auditLog: any[];
  temporalPermissions: any[];
  initializeUserProfile: () => Promise<void>;
  needsHotelSelection: boolean;
};

const OptimizedHotelContext = createContext<OptimizedHotelContextType | undefined>(undefined);

type Props = {
  children: ReactNode;
};

export const OptimizedHotelProvider = ({ children }: Props) => {
  // ⚡ AK-47: Берём данные из AuthContext вместо дублирующих запросов
  const authContext = useContext(AuthContext);
  const user = authContext?.user ?? null;
  const systemRole = authContext?.systemRole ?? null;
  const authLoading = authContext?.loading ?? false;
  const permissionsLoading = authContext?.permissionsLoading ?? false;
  
  const [currentHotel, setCurrentHotel] = useState<Hotel | null>(null);
  const [hotels, setHotels] = useState<Hotel[]>([]);
  const [loading, setLoading] = useState(true);
  const [hotelReady, setHotelReady] = useState(false);
  const [canSwitchHotels, setCanSwitchHotels] = useState(false);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [needsHotelSelection, setNeedsHotelSelection] = useState(false);
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const prevHotelIdRef = useRef<string | null>(null);

  const DEFAULT_HOTEL_ID = 'c7d8e9f0-5678-9012-3456-789012345678';
  const HOTEL_STORAGE_KEY = 'selected_hotel_id';
  const getSavedHotelId = () => {
    try {
      return localStorage.getItem(HOTEL_STORAGE_KEY);
    } catch {
      return null;
    }
  };

  const saveHotelId = (hotelId: string) => {
    try {
      localStorage.setItem(HOTEL_STORAGE_KEY, hotelId);
    } catch {
      // Ignore localStorage errors
    }
  };

  // ⚡ AK-47: checkUserRole удалён — роль читается из useAuth().systemRole (0 сетевых запросов)

  // 🚀 Установка отеля с очисткой кэша при переключении
  const setHotelStable = async (hotel: Hotel) => {
    // 🔒 ИЗОЛЯЦИЯ: Очистка кэша при смене отеля для предотвращения утечки данных
    const isSwitch = prevHotelIdRef.current && prevHotelIdRef.current !== hotel.id;
    
    // 🔒 ИЗОЛЯЦИЯ: Очистка localStorage при смене отеля
    if (isSwitch) {
      const keysToRemove = [
        'voice_chat_history',
        'draft_booking_data',
        'cached_rooms_data',
        'cached_bookings_data',
      ];
      keysToRemove.forEach(key => {
        try { localStorage.removeItem(key); } catch { /* ignore */ }
      });
    }
    // prevHotelIdRef обновляется в useEffect ниже

    try {
      const { error: configError } = await supabase.rpc('perform_set_config', {
        parameter: 'app.current_hotel_id',
        value: hotel.id
      });

      if (configError) {
        toast({
          title: "Предупреждение",
          description: "Не удалось установить контекст отеля в базе данных",
          variant: "destructive",
        });
      }
    } catch {
      // Silent fail
    }

    setCurrentHotel(hotel);
    saveHotelId(hotel.id);
    setNeedsHotelSelection(false);
    setHotelReady(true);

  };


  // 🚀 Загрузка отелей без лишних логов
  const loadHotels = async () => {
    // 🆕 ОПТИМИЗАЦИЯ: Пропускаем загрузку для widget routes (они используют свой запрос)
    const isWidgetRoute = window.location.pathname.startsWith('/booking/') || 
                          window.location.pathname.startsWith('/oscar/') ||
                          window.location.pathname.includes('widget.html');
    if (isWidgetRoute) {
      console.log('⏭️ OptimizedHotelContext: Пропуск для widget route');
      setLoading(false);
      setHotelReady(true);
      return;
    }
    
    try {
      setLoading(true);
      setHotelReady(false);

      const hostname = window.location.hostname;
      const parts = hostname.split('.');
      
      if (parts.length >= 3 && parts[1] === 'hotel3d') {
        const subdomain = parts[0];
        
        const { data: subdomainHotel, error: subdomainError } = await supabase
          .from('hotels')
          .select('id, name, slug, subdomain, is_active')
          .eq('is_active', true)
          .or(`subdomain.eq.${subdomain},slug.eq.${subdomain}`)
          .maybeSingle();
        
        if (!subdomainError && subdomainHotel) {
          await setHotelStable(subdomainHotel);
          setHotels([subdomainHotel]);
          return;
        }
      }

      // ⚡ AK-47: user и роль уже в памяти из AuthContext (0 сетевых запросов)
      if (!user) {
        await setDefaultHotel();
        return;
      }

      const isSuperAdminUser = systemRole === 'super_admin' || systemRole === 'platform_owner';
      setIsSuperAdmin(isSuperAdminUser);
      setCanSwitchHotels(isSuperAdminUser);

      if (isSuperAdminUser) {
        const { data: allHotels, error } = await supabase
          .from('hotels')
          .select('id, name, slug, subdomain, is_active')
          .eq('is_active', true)
          .order('name');

        if (error) throw error;
        setHotels(allHotels || []);

        if (allHotels && allHotels.length > 0) {
          const savedHotelId = getSavedHotelId();
          let selectedHotel = savedHotelId 
            ? allHotels.find(h => h.id === savedHotelId) 
            : null;
          
          if (!selectedHotel) {
            selectedHotel = allHotels.find(h => h.id === DEFAULT_HOTEL_ID) || allHotels[0];
          }

          await setHotelStable(selectedHotel);
        } else {
          setNeedsHotelSelection(true);
          setHotelReady(true);
        }
      } else {
        const { data: profiles, error } = await supabase
          .from('user_profiles')
          .select(`
            hotel_id,
            hotels!inner(id, name, slug, subdomain, is_active)
          `)
          .eq('user_id', user.id);

        if (error) throw error;

        if (profiles && profiles.length > 0) {
          const userHotels = profiles.map(p => p.hotels).filter(Boolean);
          setHotels(userHotels);
          
          if (userHotels.length > 0) {
            await setHotelStable(userHotels[0]);
          }
        } else {
          await setDefaultHotel();
        }
      }
    } catch {
      toast({
        title: "Ошибка",
        description: "Не удалось загрузить отели. Используем отель по умолчанию.",
        variant: "destructive",
      });
      await setDefaultHotel();
    } finally {
      setLoading(false);
    }
  };

  const setDefaultHotel = async () => {
    // 🔒 БЕЗОПАСНОСТЬ: Авторизованные пользователи без отеля НЕ получают fallback на Оскар
    // DEFAULT_HOTEL_ID используется ТОЛЬКО для виджет-режима (неавторизованные)
    if (user) {
      // Авторизованный пользователь без привязки к отелю — показываем "нужно выбрать отель"
      setNeedsHotelSelection(true);
      setHotelReady(true);
      setLoading(false);
      return;
    }

    // Неавторизованный (виджет) — подключаем к дефолтному отелю
    try {
      const { data: defaultHotel, error } = await supabase
        .from('hotels')
        .select('id, name, slug, subdomain, is_active')
        .eq('id', DEFAULT_HOTEL_ID)
        .single();

      if (error) throw error;

      if (defaultHotel) {
        await setHotelStable(defaultHotel);
        setHotels([defaultHotel]);
      }
    } catch {
      const fallbackHotel = {
        id: DEFAULT_HOTEL_ID,
        name: 'Отель Оскар',
        slug: 'hotel-oscar',
        is_active: true
      };
      await setHotelStable(fallbackHotel);
      setHotels([fallbackHotel]);
    }
  };

  const switchHotel = async (hotelId: string) => {
    console.log('🔄 switchHotel called:', { hotelId, canSwitchHotels, isSuperAdmin, systemRole, hotelsCount: hotels.length });
    
    if (!canSwitchHotels) {
      console.error('❌ switchHotel BLOCKED: canSwitchHotels=false, systemRole=', systemRole);
      toast({ title: "Ошибка", description: "У вас нет прав для переключения отелей", variant: "destructive" });
      return;
    }

    const hotel = hotels.find(h => h.id === hotelId);
    if (!hotel) {
      console.error('❌ switchHotel BLOCKED: hotel not found in list', { hotelId, availableIds: hotels.map(h => h.id) });
      toast({ title: "Ошибка", description: "Отель не найден", variant: "destructive" });
      return;
    }

    console.log('✅ switchHotel: saving and reloading for', hotel.name);

    // 1. Сохраняем в localStorage
    saveHotelId(hotel.id);

    // 2. Устанавливаем RPC контекст в БД
    try {
      await supabase.rpc('perform_set_config', {
        parameter: 'app.current_hotel_id',
        value: hotel.id
      });
    } catch { /* ignore */ }

    // 3. АК-47: Перезагрузка страницы
    window.location.reload();
  };

  const refreshHotels = async () => {
    await loadHotels();
  };

  const initializeUserProfile = async () => {
    try {
      const { error } = await supabase.rpc('initialize_user_profile');
      
      if (error) {
        toast({
          title: "Ошибка",
          description: "Не удалось инициализировать профиль пользователя",
          variant: "destructive",
        });
        return;
      }
      
      toast({
        title: "Успешно",
        description: "Профиль пользователя инициализирован",
      });
      
      await refreshHotels();
    } catch {
      toast({
        title: "Ошибка",
        description: "Произошла ошибка при инициализации профиля",
        variant: "destructive",
      });
    }
  };

  // ⚡ AK-47: Ждём готовности AuthContext перед загрузкой отелей
  useEffect(() => {
    // 🎯 Виджет работает без AuthProvider — пропускаем auth guard
    const isWidget = window.location.pathname.includes('widget.html')
      || window.location.hash.includes('/booking/')
      || window.location.pathname.startsWith('/booking/');
    if (isWidget) {
      setLoading(false);
      setHotelReady(true);
      setDefaultHotel(); // данные подтянутся в фоне
      return;
    }
    
    console.log('🏨 HotelContext useEffect:', { authLoading, permissionsLoading, userId: user?.id, systemRole });
    // Ждём ПОЛНОЙ готовности AuthContext: и user, и systemRole
    if (authLoading || permissionsLoading) {
      console.log('⏳ HotelContext: waiting for auth...', { authLoading, permissionsLoading });
      return;
    }
    
    // АК-47: Если юзер есть, но роль ещё не определена — ЖДЁМ
    if (user && !systemRole) {
      console.log('⏳ HotelContext: user exists but systemRole is null, waiting...');
      return;
    }
    
    if (user) {
      console.log('🚀 HotelContext: loadHotels with systemRole=', systemRole);
      loadHotels();
    } else {
      setDefaultHotel().finally(() => {
        setLoading(false);
        setHotelReady(true);
      });
    }
  }, [authLoading, permissionsLoading, user?.id, systemRole]);

  // ⚡ AK-47: Стабилизируем value, чтобы потребители контекста не перерисовывались
  // при каждом ре-рендере провайдера. Симптом до фикса: HotelSelectionModal
  // рендерился дважды с интервалом ~1с (идентичные дампы 13 отелей в консоли).
  const value = useMemo(() => ({
    currentHotel,
    hotels,
    loading,
    hotelReady,
    isLoading: loading,
    availableHotels: hotels,
    userProfile: null,
    switchHotel,
    refreshHotels,
    refreshContext: refreshHotels,
    canSwitchHotels,
    isSuperAdmin,
    riskScore: 0,
    auditLog: [],
    temporalPermissions: [],
    initializeUserProfile,
    needsHotelSelection,
  }), [
    currentHotel,
    hotels,
    loading,
    hotelReady,
    canSwitchHotels,
    isSuperAdmin,
    needsHotelSelection,
  ]);

  return (
    <OptimizedHotelContext.Provider value={value}>
      {children}
    </OptimizedHotelContext.Provider>
  );
};

export const useOptimizedHotel = () => {
  const context = useContext(OptimizedHotelContext);
  if (context === undefined) {
    throw new Error('useOptimizedHotel must be used within an OptimizedHotelProvider');
  }
  return context;
};
